import React, { useState, useEffect } from 'react';
import { MessageSquare, Send, Clock, CheckCircle, AlertCircle, Search, Mail, Phone, MessageCircle, RefreshCw, Zap } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Message {
  id: string;
  appointmentId: string;
  patientId: string;
  type: 'sms' | 'email' | 'voice';
  content: string;
  sentAt: string;
  status: 'sent' | 'delivered' | 'failed';
  scheduledFor?: string;
  externalId?: string;
  patient?: {
    patient_id: string;
    email?: string;
    phone?: string;
  };
  appointment?: {
    appointment_date: string;
    appointment_time: string;
    department: string;
    visit_type: string;
  };
}

export const Messages: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<'all' | 'sms' | 'email' | 'voice'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'sent' | 'delivered' | 'failed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [processingReminders, setProcessingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState<{ sent: number; failed: number; message: string } | null>(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      
      // Check if Supabase is configured
      if (!import.meta.env.VITE_SUPABASE_URL || !import.meta.env.VITE_SUPABASE_ANON_KEY) {
        // Return mock data for demo
        const mockMessages: Message[] = [
          {
            id: '1',
            appointmentId: 'apt-001',
            patientId: 'pat-001',
            type: 'sms',
            content: 'Reminder: You have an appointment tomorrow at 2:00 PM with Dr. Smith in Cardiology. Please reply CONFIRM to confirm your attendance.',
            sentAt: '2024-12-03T10:30:00Z',
            status: 'delivered',
            patient: { patient_id: 'PAT-001', phone: '+1234567890' },
            appointment: { appointment_date: '2024-12-04', appointment_time: '14:00', department: 'Cardiology', visit_type: 'Follow-up' }
          },
          {
            id: '2',
            appointmentId: 'apt-002',
            patientId: 'pat-002',
            type: 'email',
            content: 'Dear Patient, this is a reminder about your upcoming appointment on December 5th at 9:00 AM in the Orthopedics department. Please arrive 15 minutes early.',
            sentAt: '2024-12-03T08:15:00Z',
            status: 'delivered',
            patient: { patient_id: 'PAT-002', email: 'patient@example.com' },
            appointment: { appointment_date: '2024-12-05', appointment_time: '09:00', department: 'Orthopedics', visit_type: 'Consultation' }
          },
          {
            id: '3',
            appointmentId: 'apt-003',
            patientId: 'pat-003',
            type: 'voice',
            content: 'Automated voice reminder for high-risk patient about appointment.',
            sentAt: '2024-12-03T16:45:00Z',
            status: 'failed',
            patient: { patient_id: 'PAT-003', phone: '+1234567891' },
            appointment: { appointment_date: '2024-12-04', appointment_time: '11:30', department: 'Psychiatry', visit_type: 'Screening' }
          },
          {
            id: '4',
            appointmentId: 'apt-004',
            patientId: 'pat-004',
            type: 'sms',
            content: 'URGENT: Your appointment is in 2 hours. Please confirm your attendance by replying YES.',
            sentAt: '2024-12-03T12:00:00Z',
            status: 'sent',
            patient: { patient_id: 'PAT-004', phone: '+1234567892' },
            appointment: { appointment_date: '2024-12-03', appointment_time: '14:00', department: 'General Medicine', visit_type: 'Routine' }
          },
          {
            id: '5',
            appointmentId: 'apt-005',
            patientId: 'pat-005',
            type: 'email',
            content: 'Thank you for confirming your appointment. We look forward to seeing you tomorrow.',
            sentAt: '2024-12-02T17:20:00Z',
            status: 'delivered',
            patient: { patient_id: 'PAT-005', email: 'patient5@example.com' },
            appointment: { appointment_date: '2024-12-03', appointment_time: '10:00', department: 'Pediatrics', visit_type: 'Check-up' }
          }
        ];
        
        setMessages(mockMessages);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('messages')
        .select(`
          *,
          patients (
            patient_id,
            email,
            phone
          ),
          appointments (
            appointment_date,
            appointment_time,
            department,
            visit_type
          )
        `)
        .order('sent_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      const formattedMessages: Message[] = data?.map(msg => ({
        id: msg.id,
        appointmentId: msg.appointment_id,
        patientId: msg.patient_id,
        type: msg.type,
        content: msg.content,
        sentAt: msg.sent_at,
        status: msg.status,
        scheduledFor: msg.scheduled_for,
        externalId: msg.external_id,
        patient: msg.patients,
        appointment: msg.appointments
      })) || [];

      setMessages(formattedMessages);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const processReminders = async () => {
    if (!confirm('Process all due reminders now? This will send real emails and SMS messages based on your configured rules.')) {
      return;
    }

    setProcessingReminders(true);
    setReminderResult(null);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-reminders`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        setReminderResult({ sent: 0, failed: 0, message: data.error || 'Failed to process reminders' });
        return;
      }

      setReminderResult({
        sent: data.sent || 0,
        failed: data.failed || 0,
        message: data.message || 'Processing complete',
      });

      await fetchMessages();
    } catch (error) {
      console.error('Error processing reminders:', error);
      setReminderResult({ sent: 0, failed: 0, message: 'Network error - failed to process reminders' });
    } finally {
      setProcessingReminders(false);
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesType = filterType === 'all' || message.type === filterType;
    const matchesStatus = filterStatus === 'all' || message.status === filterStatus;
    const matchesSearch = searchTerm === '' || 
      message.patient?.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      message.appointment?.department.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesType && matchesStatus && matchesSearch;
  });

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'failed': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'sent': return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'sent': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'sms': return <MessageCircle className="w-4 h-4 text-blue-500" />;
      case 'email': return <Mail className="w-4 h-4 text-green-500" />;
      case 'voice': return <Phone className="w-4 h-4 text-purple-500" />;
      default: return <MessageSquare className="w-4 h-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'sms': return 'bg-blue-100 text-blue-800';
      case 'email': return 'bg-green-100 text-green-800';
      case 'voice': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const messageStats = {
    total: messages.length,
    delivered: messages.filter(m => m.status === 'delivered').length,
    failed: messages.filter(m => m.status === 'failed').length,
    pending: messages.filter(m => m.status === 'sent').length,
    sms: messages.filter(m => m.type === 'sms').length,
    email: messages.filter(m => m.type === 'email').length,
    voice: messages.filter(m => m.type === 'voice').length
  };

  const deliveryRate = messageStats.total > 0 ? 
    Math.round((messageStats.delivered / messageStats.total) * 100) : 0;

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-8"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-20 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Message Center</h1>
          <p className="text-gray-600 mt-2">Monitor and manage all patient communications</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchMessages}
            className="flex items-center space-x-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Refresh</span>
          </button>
          <button
            onClick={processReminders}
            disabled={processingReminders}
            className="flex items-center space-x-2 px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Zap className="w-4 h-4" />
            <span>{processingReminders ? 'Processing...' : 'Process Reminders Now'}</span>
          </button>
        </div>
      </div>

      {reminderResult && (
        <div className={`mb-6 p-4 rounded-lg border flex items-start gap-3 ${reminderResult.failed > 0 && reminderResult.sent === 0 ? 'bg-red-50 border-red-200' : reminderResult.sent > 0 ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
          {reminderResult.sent > 0 ? (
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
          )}
          <div>
            <p className="font-medium text-gray-900">{reminderResult.message}</p>
            {(reminderResult.sent > 0 || reminderResult.failed > 0) && (
              <p className="text-sm text-gray-600 mt-1">
                {reminderResult.sent} sent &bull; {reminderResult.failed} failed
              </p>
            )}
          </div>
          <button onClick={() => setReminderResult(null)} className="ml-auto text-gray-400 hover:text-gray-600 text-lg leading-none">&times;</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Messages</p>
              <p className="text-2xl font-bold text-gray-900">{messageStats.total}</p>
            </div>
            <MessageSquare className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Delivery Rate</p>
              <p className="text-2xl font-bold text-green-600">{deliveryRate}%</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Failed Messages</p>
              <p className="text-2xl font-bold text-red-600">{messageStats.failed}</p>
            </div>
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{messageStats.pending}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-600" />
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border mb-8">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search messages..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="voice">Voice</option>
              </select>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="delivered">Delivered</option>
                <option value="sent">Sent</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-2">
                <MessageCircle className="w-4 h-4 text-blue-500" />
                <span>SMS: {messageStats.sms}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4 text-green-500" />
                <span>Email: {messageStats.email}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Phone className="w-4 h-4 text-purple-500" />
                <span>Voice: {messageStats.voice}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages List */}
        <div className="divide-y divide-gray-200">
          {filteredMessages.length === 0 ? (
            <div className="p-12 text-center">
              <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Messages Found</h3>
              <p className="text-gray-600">
                {searchTerm || filterType !== 'all' || filterStatus !== 'all' 
                  ? 'Try adjusting your filters or search terms.'
                  : 'No messages have been sent yet.'}
              </p>
            </div>
          ) : (
            filteredMessages.map((message) => (
              <div key={message.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    {getTypeIcon(message.type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900">
                          {message.patient?.patient_id || 'Unknown Patient'}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(message.type)}`}>
                          {message.type.toUpperCase()}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(message.status)}`}>
                          {getStatusIcon(message.status)}
                          <span className="ml-1">{message.status}</span>
                        </span>
                      </div>
                      <div className="text-sm text-gray-500">
                        {new Date(message.sentAt).toLocaleString()}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <p className="text-gray-800 text-sm leading-relaxed">
                        {message.content}
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <div className="flex items-center space-x-4">
                        {message.appointment && (
                          <>
                            <span>
                              📅 {message.appointment.appointment_date} at {message.appointment.appointment_time}
                            </span>
                            <span>
                              🏥 {message.appointment.department}
                            </span>
                            <span>
                              📋 {message.appointment.visit_type}
                            </span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center space-x-2">
                        {message.type === 'sms' && message.patient?.phone && (
                          <span>📱 {message.patient.phone}</span>
                        )}
                        {message.type === 'email' && message.patient?.email && (
                          <span>✉️ {message.patient.email}</span>
                        )}
                        {message.externalId && (
                          <span className="font-mono">ID: {message.externalId.slice(-8)}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600">
            Showing {filteredMessages.length} of {messages.length} messages
          </p>
        </div>
      </div>
    </div>
  );
};